import { Button } from "primereact/button";
import { type FC, useState } from "react";
import { useForm } from "react-hook-form";
import { requestPasswordReset } from "@/apis/clients/sessionClient";
import AuthCard, { submitButtonPt } from "@/components/AuthCard";
import authStyles from "@/components/AuthCard.module.css";
import BackToLoginLink from "@/components/BackToLoginLink";
import { TEXT } from "@/constants/text";
import EmailField from "@/pages/auth/EmailField";
import styles from "@/pages/auth/ResetPasswordPage/ResetPasswordForm.module.css";
import { isStaffEmail } from "@/utils/validation";

interface ResetPasswordFormValues {
  email: string;
}

const ResetPasswordForm: FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>();

  // The confirmation is shown only after the request is actually made. It
  // previously appeared on submit alone, which meant the page promised a reset
  // link that nothing had asked for.
  const onSubmit = async (values: ResetPasswordFormValues) => {
    try {
      await requestPasswordReset(values.email);
    } catch {
      // The endpoint answers 204 for every address, so reaching here means the
      // request never arrived — not that the address was rejected. Saying so
      // beats the confirmation screen, which would promise an email that
      // nothing asked for; and beats silence, which looks like a dead button.
      setError("root", {
        message: TEXT.auth.resetPassword.requestUnavailable,
      });
      return;
    }
    setSubmitted(true);
  };

  const emailValue = watch("email") ?? "";
  const isEnterpriseEmail = isStaffEmail(emailValue);

  if (submitted) {
    return (
      <div className={styles.container}>
        <AuthCard
          beforeTitle={
            <BackToLoginLink label={TEXT.auth.resetPassword.backToLoginLink} />
          }
        >
          <p className={authStyles.subtitle}>
            {TEXT.auth.resetPassword.confirmationMessage}
          </p>
        </AuthCard>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AuthCard
        beforeTitle={
          <BackToLoginLink label={TEXT.auth.resetPassword.backToLoginLink} />
        }
        subtitle={TEXT.auth.resetPassword.subtitle}
        title={TEXT.auth.resetPassword.title}
      >
        <form
          className={`${authStyles.form} p-fluid`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <EmailField
            error={errors.email}
            invalidMessage={TEXT.auth.resetPassword.emailInvalid}
            label={TEXT.auth.resetPassword.emailLabel}
            register={register}
            requiredMessage={TEXT.auth.resetPassword.emailRequired}
          />
          {isEnterpriseEmail && (
            <p className={authStyles.error} role="alert">
              {TEXT.auth.resetPassword.enterpriseEmailNotice}
            </p>
          )}
          {errors.root && (
            <span className={authStyles.error} role="alert">
              {errors.root.message}
            </span>
          )}
          <Button
            disabled={isEnterpriseEmail || isSubmitting}
            icon="pi pi-arrow-right"
            iconPos="right"
            label={TEXT.auth.resetPassword.submitButton}
            pt={submitButtonPt}
            type="submit"
          />
        </form>
      </AuthCard>
    </div>
  );
};

export default ResetPasswordForm;
